import { DocService } from '@affine/core/modules/doc';
import { AccessibleFolderCacheStore } from '@affine/core/modules/organize';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { isDocInsidePrivateSystemFolder } from './doc-share-restrictions';

export function useDocShareRestrictions() {
  const docService = useService(DocService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(accessibleFolderCache.watchAllAccessibleFolders(), []),
      [accessibleFolderCache]
    )
  );
  const isLocalCacheLoaded = useLiveData(
    accessibleFolderCache.isLocalCacheLoaded$
  );
  const isPopulated = useLiveData(accessibleFolderCache.isPopulated$);
  const isReady = isLocalCacheLoaded === true && isPopulated === true;
  const [timedOut, setTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (isReady) {
      setTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setTimedOut(true), 15_000);
    return () => clearTimeout(timer);
  }, [isReady, attempt]);
  const retry = useCallback(() => {
    setTimedOut(false);
    setAttempt(value => value + 1);
    void accessibleFolderCache
      .fetchAccessibleFolders(AbortSignal.timeout(15_000))
      .catch(() => setTimedOut(true));
  }, [accessibleFolderCache]);
  const privateFolderDocShareBlocked = useMemo(
    () =>
      isReady &&
      isDocInsidePrivateSystemFolder(docService.doc.id, accessibleFolders),
    [accessibleFolders, docService.doc.id, isReady]
  );

  return {
    isReady,
    privateFolderDocShareBlocked,
    timedOut: !isReady && timedOut,
    retry,
  };
}
